import { API_BASE_URL, STORAGE_KEYS } from '../constants';
import { AUTH_ENDPOINTS } from './endpoints';
import { authEvents } from './authEvents';

interface RequestConfig extends RequestInit {
  requiresAuth?: boolean;
}

interface ApiError {
  message: string;
  code?: number;
}

interface TokenInfo {
  token: string;
  expires: string;
}

interface Tokens {
  access: TokenInfo;
  refresh: TokenInfo;
}

class ApiClient {
  private baseUrl: string;
  private isRefreshing: boolean = false;
  private refreshPromise: Promise<boolean> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  private getAuthHeaders(): HeadersInit {
    const token = this.getAccessToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  private clearStorage(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
  }

  private saveTokens(tokens: Tokens): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access.token);
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh.token);
  }

  private async refreshAccessToken(): Promise<boolean> {
    // If already refreshing, wait for the existing refresh to complete
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return false;
    }

    this.isRefreshing = true;
    this.refreshPromise = this.doRefreshToken(refreshToken);

    try {
      return await this.refreshPromise;
    } finally {
      this.isRefreshing = false;
      this.refreshPromise = null;
    }
  }

  private async doRefreshToken(refreshToken: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseUrl}${AUTH_ENDPOINTS.REFRESH_TOKENS}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        return false;
      }

      const tokens = (await response.json()) as Tokens;
      this.saveTokens(tokens);
      return true;
    } catch {
      return false;
    }
  }

  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const { requiresAuth = false, headers = {}, ...restConfig } = config;

    const isFormData = restConfig.body instanceof FormData;
    const requestHeaders: HeadersInit = {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      ...headers,
      ...(requiresAuth ? this.getAuthHeaders() : {}),
    };

    let response: Response;
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...restConfig,
        headers: requestHeaders,
      });
    } catch {
      const error: ApiError = {
        message: 'Unable to connect to server. Please check your internet connection.',
        code: 0,
      };
      throw error;
    }

    // Handle 401 Unauthorized - attempt token refresh
    if (response.status === 401 && requiresAuth) {
      const refreshed = await this.refreshAccessToken();

      if (refreshed) {
        // Retry the original request with new token
        const retryHeaders: HeadersInit = {
          ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          ...headers,
          ...this.getAuthHeaders(),
        };

        let retryResponse: Response;
        try {
          retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
            ...restConfig,
            headers: retryHeaders,
          });
        } catch {
          const error: ApiError = {
            message: 'Unable to connect to server. Please check your internet connection.',
            code: 0,
          };
          throw error;
        }

        if (retryResponse.ok) {
          return (await retryResponse.json()) as T;
        }

        // If retry also fails with 401, clear session and emit logout
        if (retryResponse.status === 401) {
          this.clearStorage();
          authEvents.emitLogout();
          const error: ApiError = {
            message: 'Session expired. Please log in again.',
            code: 401,
          };
          throw error;
        }

        const retryData = await retryResponse.json();
        const error: ApiError = {
          message: retryData.message || 'An error occurred',
          code: retryResponse.status,
        };
        throw error;
      }

      // Refresh failed - clear session and emit logout
      this.clearStorage();
      authEvents.emitLogout();
      const error: ApiError = {
        message: 'Session expired. Please log in again.',
        code: 401,
      };
      throw error;
    }

    let data: unknown;
    try {
      data = await response.json();
    } catch {
      if (!response.ok) {
        const error: ApiError = {
          message: 'Server error. Please try again later.',
          code: response.status,
        };
        throw error;
      }
      // If response is OK but no JSON body, return empty object
      return {} as T;
    }

    if (!response.ok) {
      const error: ApiError = {
        message: (data as { message?: string }).message || 'An error occurred',
        code: response.status,
      };
      throw error;
    }

    return data as T;
  }

  get<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  post<T>(endpoint: string, body: unknown, config: RequestConfig = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  put<T>(endpoint: string, body: unknown, config: RequestConfig = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  patch<T>(endpoint: string, body: unknown, config: RequestConfig = {}): Promise<T> {
    return this.request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  delete<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }

  postForm<T>(endpoint: string, formData: FormData, config: RequestConfig = {}): Promise<T> {
    return this.request<T>(endpoint, {
      requiresAuth: true,
      ...config,
      method: 'POST',
      body: formData,
    });
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
